<template>
    <li :id="`item-${item.id}`" :class="getClasses">
        <BLItemForm
            v-if="inEdit"
            :initial-values="item"
            :bucket-list="bucketList"
            :item="item"
            :errors="errors"
            @submit-form="updateItem"
            @cancelled="inEdit = false"
        />
        <template v-else>
            <div class="label-wrapper">
                <div v-if="item.image_url" class="bucket-list-item-image">
                    <ExternalImage :src="item.image_url" />
                </div>
                <label>
                    <input
                        v-if="!editMode"
                        :id="item.id"
                        v-model="ticked"
                        class="hidden"
                        type="checkbox"
                        @input="(event) => tick(event, item)"
                    />
                    <div
                        :class="['custom-checkbox', { checked: item.checked }]"
                    >
                        <span
                            v-if="item.checked"
                            class="icon material-symbols-outlined"
                        >
                            done
                        </span>
                    </div>
                    <span class="label-text">
                        {{ name
                        }}<span
                            v-if="bucketList.type !== 'country' && item.geo"
                            class="label-subtext"
                            >, {{ item.geo.country }}</span
                        >
                    </span>
                </label>
            </div>
            <ImpressionWidget
                v-if="!editing"
                :enabled="item.checked"
                :value="item.impression"
                :edit-mode="editMode"
                @updated="updateImpression(item.id, $event)"
            />
            <button v-if="editing" class="edit-button" @click="enterEditItem">
                <span class="icon material-symbols-outlined">edit</span>
            </button>
            <span v-if="item.notices.visited" class="notice-text">
                {{ item.notices.visited.message }}
                <router-link :to="`/trips/${item.notices.visited.related_id}`">
                    {{ item.notices.visited.related_name }}
                </router-link>
            </span>
            <button
                v-if="deleting"
                class="icon-button delete-button action-button"
                @click="deletePending = true"
            >
                <span class="icon material-symbols-outlined">delete</span>
            </button>
            <button
                v-if="deletePending && deleting"
                class="icon-button cancel-delete-button action-button"
                @click="deletePending = false"
            >
                <span class="material-symbols-outlined">cancel</span>
            </button>
            <button
                v-if="deletePending && deleting"
                class="icon-button confirm-delete-button action-button"
                @click="deleteItem(item)"
            >
                <span class="material-symbols-outlined">task_alt</span>
            </button>
            <div
                v-else-if="sorting"
                class="sorting-indication"
                :class="item.image_url ? 'with-image' : ''"
            >
                <span class="material-symbols-outlined">more_vert</span>
            </div>
        </template>
    </li>
</template>
<script>
import { useBucketListStore } from "js/stores/bucketListStore";
import ImpressionWidget from "js/components/widgets/ImpressionWidget";
import BLItemForm from "js/components/forms/BLItemForm";
import BaseFormWrapper from "js/components/base/BaseFormWrapper";
import ExternalImage from "js/components/common/ExternalImage.vue";

export default {
    name: "BLItem",
    components: {
        ImpressionWidget,
        BLItemForm,
        ExternalImage,
    },
    extends: BaseFormWrapper,
    inject: ["screenWidth"],
    props: {
        bucketList: {
            type: Object,
            required: true,
        },
        item: {
            type: Object,
            required: true,
        },
        name: {
            type: String,
            required: true,
        },
        sorting: {
            type: Boolean,
            default: false,
        },
        deleting: {
            type: Boolean,
            default: false,
        },
        editing: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["updated"],
    data() {
        return {
            store: undefined,
            ticked: undefined,
            deletePending: false,
            errors: {},
            inEdit: false,
        };
    },
    computed: {
        editMode() {
            return this.deleting || this.sorting;
        },
        getClasses() {
            let classes = [
                this.inEdit ? "bucket-list-item-edit" : "bucket-list-item",
                { wide: this.bucketList.type === "custom" },
            ];
            if (!this.inEdit) {
                classes.push({
                    "edit-mode": this.editMode,
                    sorting: this.sorting,
                    deleting: this.deleting,
                    "confirm-deleting": this.deletePending && this.deleting,
                });
            }
            return classes;
        },
    },
    mounted() {
        this.store = useBucketListStore();
        this.ticked = this.item.checked;
    },
    methods: {
        async tick(event, item) {
            this.ticked = event.target.checked;

            let bli = await this.store.edit(
                `/api/bucket-lists/${this.bucketList.id}/items/${item.id}`,
                { checked: event.target.checked }
            );

            if (!bli) {
                return;
            }

            this.store.replaceItem(this.bucketList.id, item.id, bli);

            if (bli.notices?.visited) {
                delete bli.notices.visited;
            }

            this.$emit("updated");
        },
        deleteItem(item) {
            this.store
                .delete(
                    `/api/bucket-lists/${this.bucketList.id}/items/${item.id}`
                )
                .then(() => {
                    this.store.find(this.bucketList.id).items =
                        this.bucketList.items.filter((otherItem) => {
                            return otherItem.id !== item.id;
                        });
                    this.$emit("updated");
                });
        },
        updateImpression(itemId, impression) {
            this.store
                .edit(
                    `/api/bucket-lists/${this.bucketList.id}/items/${itemId}/`,
                    {
                        impression,
                    }
                )
                .then((data) => {
                    this.store.replaceItem(this.bucketList.id, itemId, data);
                });
        },
        updateItem(data) {
            this.store
                .edit(
                    `/api/bucket-lists/${this.bucketList.id}/items/${this.item.id}/`,
                    data,
                    !!data.user_image_file
                )
                .then((data) => {
                    this.store.replaceItem(
                        this.bucketList.id,
                        this.item.id,
                        data
                    );
                    this.inEdit = false;
                })
                .catch(this.handleResponseErrors);
        },
        enterEditItem() {
            this.inEdit = true;
        },
    },
};
</script>
